import { RestUseCase } from "service/api/rest/rest-usecase"
import { EntityField } from "../../../@types/entity/field/crud/field-crud-entity"
import { CompaniesSelect } from "../../../@types/entity/interface/select-option-interface"
import { TableComponents } from "../../../@types/entity/interface/table-interface"
import { HeaderNavegationList } from "helper/adapter/headerNavigation/header-navegation-adapter"
import { ParseDateToEn } from "helper/format-date-helper"
import { UseAlert } from "hook/alert-hook"
import { UseAuth } from "hook/auth-hook"
import ErrorNotAuthorizedPage from "pages/layout/error/error-not-authorized-page"
import UserPageV2 from "pages/layout/safety/user-page-v2"
import { ReactNode, useEffect, useMemo, useState } from "react"
import { AiFillLock } from "react-icons/ai";
import { getEnv } from "helper/window-helper";
import { AccessIs, hasAnyAccess } from "helper/access-permission";
import { deveExibirBotaoVinculoLoja } from "helper/role-helper";
import { readFluxoAprovacaoCompletoFlag } from "helper/fluxo-aprovacao-empresa-helper";
import { UsuarioDTO } from "../../../@types/interfaces/safety/usuario-dto"

type UserEditResponse = {
    message?: string
    loginChanged?: boolean
    forcedPasswordResetTriggered?: boolean
    updatedLogin?: string
}

export const UserFactoryV2: React.FC = () => {

    /** ENDPOINT */
    const ENDPOINT_CRUD = getEnv("REACT_APP_END_POINT_USER")!
    const ENDPOINT_LOG_USER = getEnv("REACT_APP_END_POINT_USER_LOG")!
    const ENDPOINT_COMPANIES = getEnv("REACT_APP_END_POINT_COMPANIES")!
    const ENDPOINT_RESET_PASSWORD = getEnv("REACT_APP_END_POINT_USER_RESET_PASSWORD_REQUEST")!
    const ENDPOINT_PERFIS = getEnv("REACT_APP_END_POINT_PERFIS")!

    /** USUARIO LOGADO */
    const { loggedUserData } = UseAuth()
    const canEditLogin = hasAnyAccess(loggedUserData?.access, [AccessIs.editarLogin])
    const [purchaseLinksEnabled, setPurchaseLinksEnabled] = useState(false)
    const showPurchaseLinks = deveExibirBotaoVinculoLoja(loggedUserData?.access, purchaseLinksEnabled)

    useEffect(() => {
        let active = true
        readFluxoAprovacaoCompletoFlag(loggedUserData?.companyId).then(flag => {
            if (active) setPurchaseLinksEnabled(flag === true)
        })
        return () => { active = false }
    }, [loggedUserData?.companyId])

    /** ENTIDADE PARA A TABELA E MODAL */
    const entityFields: EntityField[] = [
        { name: "id", labelField: "", labelTable: "ID", typeField: "hidden", valueField: "", sizeField: 0, required: false, align: "left", tableField: { column: "id", columnVisible: true }, sort: true, filter: true },
        { name: "idEmpresa", labelField: "Empresa:", labelTable: "Empresa", valueField: null, typeField: "select", sizeField: 6, required: false, align: "left", tableField: { column: "Texto", columnVisible: false }, labelFieldDisable: true, filter: true, sort: true },
        { name: "cpf", labelField: "CPF:", labelTable: "CPF", typeField: "text", valueField: "", sizeField: 3, required: false, align: "center", tableField: { column: "Texto", columnVisible: true }, labelFieldDisable: false, filter: true, sort: true },
        { name: "login", labelField: "Login:", labelTable: "Login", typeField: "text", valueField: "", sizeField: 3, required: false, align: "left", tableField: { column: "Texto", columnVisible: true }, labelFieldDisable: !canEditLogin, hideFieldOnCreate: true, filter: true, sort: true },
        { name: "nome", labelField: "Nome:", labelTable: "Nome", typeField: "text", valueField: "", sizeField: 6, required: false, align: "left", tableField: { column: "Texto", columnVisible: true }, labelFieldDisable: false, sort: true, filter: true },
        { name: "email", labelField: "Email:", labelTable: "Email", typeField: "text", valueField: "", sizeField: 6, required: false, align: "center", tableField: { column: "Texto", columnVisible: true }, labelFieldDisable: false, filter: true, sort: true },
        { name: "status", labelField: "Status:", labelTable: "Status", typeField: "text", valueField: "", sizeField: 6, required: false, align: "center", tableField: { column: "Texto", columnVisible: false }, labelFieldDisable: false, filter: false, sort: false, hideFieldOnCreate: true, hideFieldOnEdit: true },
        { name: "bloqueado", labelField: "Bloqueado:", labelTable: "Bloqueado", typeField: "icon", valueField: false, sizeField: 6, required: false, align: "center", tableField: { column: "Texto", columnVisible: true }, labelFieldDisable: false, filter: false, sort: false, hideFieldOnCreate: true, hideFieldOnEdit: true },
        {
            name: "cargaAutomatica",
            labelField: "Carga Automática:",
            labelTable: "Carga Automática",
            typeField: "radio",
            valueField: "N",
            listValueField: [
                { label: "Sim", value: "S" },
                { label: "Não", value: "N" },
            ],
            sizeField: 12,
            required: false,
            align: "center",
            tableField: { column: "Carga Automática", columnVisible: false },
        },
        {
            name: "usuarioReferenciaFornecedor",
            labelField: "Usuário Referência no Fornecedor:",
            labelTable: "Usuário Referência no Fornecedor",
            typeField: "radio",
            valueField: "N",
            listValueField: [
                { label: "Sim", value: "S" },
                { label: "Não", value: "N" },
            ],
            sizeField: 12,
            required: false,
            align: "center",
            tableField: { column: "Usuário Referência no Fornecedor", columnVisible: false },
        },
        {
            name: "logAcesso",
            labelField: "Log de Acesso:",
            labelTable: "Log de Acesso",
            typeField: "radio",
            valueField: "N",
            listValueField: [
                { label: "Sim", value: "S" },
                { label: "Não", value: "N" },
            ],
            sizeField: 12,
            required: false,
            align: "center",
            tableField: { column: "Log de Acesso", columnVisible: false },
        },
    ]
    const [entity, updateEntity] = useState(entityFields)

    /** VERIFICA FLUXO DE CRIAÇÃO DE USUARIO **/
    const [isCreatingUserFlow, updateCreatingUserFlow] = useState<boolean>()

    /** SELECT */
    const [companiesSelect, updateCompaniesSelect] = useState<CompaniesSelect>()

    /** TABELA */
    const [tableComponents, updateTableComponents] = useState<TableComponents>({ tableBody: [], tableHeader: [] })

    /** PERFIL */
    const [profile, updateProfile] = useState<string[]>([])

    /** USUARIO EDIÇÃO */
    const [idUser, updateIdUser] = useState<number>()

    /** ALERTA **/
    const { showAlert } = UseAlert()

    /** VERIFICA PERMISSAO DE ACESSO */
    const [showScreen, updateShowScreen] = useState<boolean>(false)
    useEffect(() => {
        const roleScreen = HeaderNavegationList.filter(header => header.navTitle === "Configurações")[0].navItem.filter(item => item.title === "Cadastrar Usuário")[0].access

        if (loggedUserData && hasAnyAccess(loggedUserData.access, roleScreen)) {
            updateShowScreen(true)
            listAllByCompany(loggedUserData.companyId)
            listAllCompanies()
        }
    }, [loggedUserData])

    /** ATUALIZA LISTA DE EMPRESAS **/
    useMemo(() => {
        updateEntity(entityFields.map(value => {
            if (value.name === "idEmpresa") {
                value.listValueField = companiesSelect?.companies
                companiesSelect?.companies?.forEach(comp => {
                    if (comp.value === companiesSelect?.companyIdSelected) {
                        value.valueField = comp.value
                    }
                })
            }
            return value
        }))
    }, [companiesSelect?.companies])

    /** FUNÇÕES AUXILIARES **/
    function pegaIdUsuarioDoHeader(response: any) {
        let uri = response.headers["location"] === "null" ? "" : String(response.headers["location"])
        var n = uri.lastIndexOf("/");
        return uri.substring(n + 1);
    }

    function getApiErrorMessage(response: any, fallbackMessage: string) {
        if (Array.isArray(response?.body) && response.body.length > 0) {
            const apiMessage = response.body.find((error: any) => typeof error?.desc === "string" && error.desc.trim() !== "")?.desc

            if (apiMessage) {
                return apiMessage
            }
        }

        if (typeof response?.body?.message === "string" && response.body.message.trim() !== "") {
            return response.body.message
        }

        return fallbackMessage
    }

    function redirectToAuthenticationError() {
        localStorage.clear()
        window.location.replace("/error-token")
        window.location.reload()
    }

    function buildUserEditSuccessMessage(responseBody?: UserEditResponse) {
        const apiMessage = responseBody?.message?.trim()
        const message = apiMessage && apiMessage !== "" ? apiMessage : "Usuario salvo com sucesso"
        const alreadyMentionsEmail = message.toLowerCase().includes("email") || message.toLowerCase().includes("e-mail")

        if (responseBody?.loginChanged && responseBody?.forcedPasswordResetTriggered && !alreadyMentionsEmail) {
            return `${message} Um e-mail para reset obrigatorio de senha foi enviado ao usuario.`
        }

        return message
    }

    function updateLoginValueOnScreen(updatedLogin?: string) {
        if (!updatedLogin) {
            return
        }

        updateEntity(currentEntity =>
            currentEntity.map(field =>
                field.name === "login"
                    ? { ...field, valueField: updatedLogin, labelFieldDisable: !canEditLogin }
                    : field
            )
        )
    }

    /** CHAMADA BACKEND */
    const copyProfile = async (idFrom: number, idTo: number) => {
        console.log("De -> Para :", idFrom, idTo);
        await new RestUseCase(`${ENDPOINT_PERFIS}/copy-perfil?from=${idFrom}&to=${idTo}`)
            .Put()
            .then((response) => {
                if (!isCreatingUserFlow) {
                    if (response.statusCode === 204 || response.statusCode === 200) {
                        showAlert({ show: true, content: "Perfil clonado com sucesso", color: "success", time: 5000 })
                    } else {
                        showAlert({ show: true, content: "Ops!!! Algo deu errado tente novamente mais tarde...", color: "danger", time: 5000 })
                    }
                }
            })
    }

    async function listAllByCompany(companyID: number) {
        if (loggedUserData !== undefined) {
            await listAllCompanies()
            await new RestUseCase(`${ENDPOINT_CRUD}?empresa=${companyID}`)
                .Get()
                .then(response => {
                    let tableComponents: TableComponents = {
                        tableHeader: entityFields.map((value) => {
                            return {
                                name: value.name,
                                labelTable: value.labelTable,
                                align: value.align,
                                columnVisible: value.tableField.columnVisible,
                                sort: value.sort === true,
                                filter: value.filter === true
                            };
                        }),
                        tableBody: response.body.map((value: { cpf: string, email: string, id: string, idEmpresa: string, login: string, nome: string, status: string, bloqueado: ReactNode }) => {
                            if (value.status === "B") {
                                value.bloqueado = <AiFillLock />
                            } else {
                                value.bloqueado = null
                            }
                            return value
                        })
                    }
                    updateTableComponents(tableComponents)
                })
        }
    }


    /**
     * Busca um usuário por login em uma empresa específica.
     *
     * Realiza uma requisição GET para o endpoint `/empresa/{empIntCod}/login` passando o login
     * como parâmetro de consulta, retornando o objeto `UsuarioDTO` correspondente ao login informado.
     *
     * @param empIntCod - Código da empresa onde a busca será realizada.
     * @param login - Login do usuário para busca exata.
     * @returns Uma Promise que resolve para um objeto `UsuarioDTO` caso o usuário seja encontrado,
     *          ou `null` caso não seja encontrado ou ocorra erro na requisição.
     *
     * @example
     * ```ts
     * const usuario = await getUsuarioPorEmpresaELogin(123, "luiz.aquino");
     * if (usuario) {
     *     console.log(usuario.nome);
     * }
     * ```
     */
    async function getUsuarioPorEmpresaELogin(
        empIntCod: number,
        login: string
    ): Promise<UsuarioDTO | null> { // Garanta o tipo de retorno
        try {
            const response = await new RestUseCase(
                `${ENDPOINT_CRUD}/empresa/${empIntCod}/login?login=${encodeURIComponent(login)}`
            ).Get_<UsuarioDTO>();

            return response.success ? response.data : null;

        } catch (error) {
            console.error("Erro ao buscar usuário:", error);
            return null;
        }
    }

    /**
     * Busca uma lista de usuários por parte do nome em uma empresa específica.
     *
     * Realiza uma requisição GET para o endpoint `/empresa/{empIntCod}/nome` passando o nome
     * como parâmetro de consulta, retornando uma lista de objetos `UsuarioDTO` que correspondem
     * ao critério de busca (nome contendo o valor informado).
     *
     * @param empIntCod - Código da empresa onde a busca será realizada.
     * @param nome - Parte do nome do usuário para busca usando operador LIKE.
     * @returns Uma Promise que resolve para um array de `UsuarioDTO` caso a busca seja bem sucedida,
     *          ou `null` em caso de falha ou erro na requisição.
     *
     * @example
     * ```ts
     * const usuarios = await getUsuariosPorEmpresaENome(123, "maria eduarda");
     * if (usuarios) {
     *     usuarios.forEach(user => console.log(user.nome));
     * }
     * ```
     */
    async function getUsuariosPorEmpresaENome(
        empIntCod: number,
        nome: string
    ): Promise<UsuarioDTO[] | null> {
        try {
            const response = await new RestUseCase(
                `${ENDPOINT_CRUD}/empresa/${empIntCod}/nome?nome=${encodeURIComponent(nome)}`
            ).Get_<UsuarioDTO[]>();

            return response.success ? response.data : null;

        } catch (error) {
            console.error("Erro ao buscar usuários por nome:", error);
            return null;
        }
    }


    async function listAllCompanies(): Promise<any> {
        await new RestUseCase(ENDPOINT_COMPANIES).Get()
            .then((response) => {
                if (response.statusCode !== 500 && response.statusCode !== undefined) {
                    let companiesS: CompaniesSelect = {
                        companies: response.body.map((value: any) => { return { value: value.id, label: value.nome, } }),
                        companyIdSelected: companiesSelect ? companiesSelect.companyIdSelected : loggedUserData!.companyId
                    }
                    updateCompaniesSelect(companiesS)
                }
            })
    }

    return (
        <>
            {showScreen ?
                <UserPageV2
                    titlePage="Cadastrar Usuário"
                    entityFields={entity}
                    tableComponents={tableComponents!}
                    companies={companiesSelect?.companies!}
                    userCompanyID={companiesSelect?.companyIdSelected}
                    profile={profile}
                    canManagePurchaseLinks={showPurchaseLinks}
                    modelMethods={{
                        create: async function (model?: any, profile?: string[]) {
                            if (profile) {
                                updateProfile(profile)
                                updateCreatingUserFlow(true)
                                await new RestUseCase(ENDPOINT_CRUD + `/create`).Post({ data: model }).then((response) => {
                                    if (response.statusCode === 201) {
                                        var idUsuario = Number(pegaIdUsuarioDoHeader(response))
                                        updateIdUser(idUsuario)
                                        showAlert({ show: true, content: "Usuario criado com sucesso", color: "success", time: 5000 })
                                    } else {
                                        showAlert({
                                            show: true,
                                            content: getApiErrorMessage(response, "Ops!!! Algo deu errado tente novamente mais tarde..."),
                                            color: "danger",
                                            time: 5000
                                        })
                                    }
                                })
                            }
                        },
                        edit: async function (model?: any, profile?: string[]) {
                            if (profile) {
                                updateProfile(profile)
                                const response = await new RestUseCase(ENDPOINT_CRUD + `/` + idUser).Patch({ data: model })

                                if (response.statusCode === 200) {
                                    const responseBody = response.body as UserEditResponse

                                    updateLoginValueOnScreen(responseBody?.updatedLogin)
                                    showAlert({
                                        show: true,
                                        content: buildUserEditSuccessMessage(responseBody),
                                        color: "success",
                                        time: 7000
                                    })

                                    return true
                                }

                                if (response.statusCode === 400 || response.statusCode === 404) {
                                    showAlert({
                                        show: true,
                                        content: getApiErrorMessage(response, "Ops!!! Algo deu errado tente novamente mais tarde..."),
                                        color: "danger",
                                        time: 5000
                                    })

                                    return false
                                }

                                if (response.statusCode === 401) {
                                    redirectToAuthenticationError()
                                    return false
                                }

                                if (response.statusCode === 403) {
                                    showAlert({
                                        show: true,
                                        content: "Voce nao tem permissao para executar esta acao.",
                                        color: "danger",
                                        time: 5000
                                    })

                                    return false
                                }

                                showAlert({ show: true, content: "Ops!!! Algo deu errado tente novamente mais tarde...", color: "danger", time: 5000 })
                            }

                            return false
                        },
                        block: async function (id: number) {
                            await new RestUseCase(`${ENDPOINT_CRUD}/${id}`).Delete().then((response) => {
                                if (response.statusCode === 204) {
                                    showAlert({ show: true, content: "Usuario bloqueado com sucesso", color: "success", time: 5000 })
                                } else {
                                    showAlert({ show: true, content: "Ops!!! Algo deu errado tente novamente mais tarde...", color: "danger", time: 5000 })
                                }
                            })
                        },
                        unblock: async function (id: number) {
                            await new RestUseCase(`${ENDPOINT_CRUD}/${id}/reactive`).Patch().then((response) => {
                                if (response.statusCode === 204) {
                                    showAlert({ show: true, content: "Usuario desbloqueado com sucesso", color: "success", time: 5000 })
                                } else {
                                    showAlert({ show: true, content: "Ops!!! Algo deu errado tente novamente mais tarde...", color: "danger", time: 5000 })
                                }
                            })
                        },
                        getById: async function (id: number) {
                            updateIdUser(id)
                            updateProfile([])
                            await listAllCompanies()
                            await new RestUseCase(`${ENDPOINT_CRUD}/${id}`)
                                .GetById()
                                .then(response => {
                                    updateEntity(entityFields.map((value) => {

                                        if (value.name === "idEmpresa") {
                                            value.listValueField = companiesSelect?.companies
                                        }
                                        value.valueField = response.body[value.name]
                                        return value
                                    }))
                                })
                            await new RestUseCase(`${ENDPOINT_CRUD}/${id}/perfis`)
                                .Get()
                                .then(response => {
                                    if (response.statusCode === 200) {
                                        updateProfile(response.body.map((value: any) => {
                                            return value.role
                                        }))
                                    }
                                })
                        },
                        listAllByCompany: listAllByCompany,
                        getUsuariosPorEmpresaENome: getUsuariosPorEmpresaENome,
                        copyProfile: copyProfile,
                        handleListLogUser: async function (dateInit, dateEnd) {
                            return await new RestUseCase(ENDPOINT_LOG_USER + `/${idUser}?inicio=${ParseDateToEn(dateInit)}&fim=${ParseDateToEn(dateEnd)}`)
                                .Get().finally()
                        },
                        resetPassword: async function (params: { userEmail: string, userLogin?: string }) {
                            let query = `?email=${params.userEmail}`

                            if (params.userLogin !== null && params.userLogin !== undefined && params.userLogin !== "") {
                                query += `&login=${params.userLogin}`
                            }
                            await new RestUseCase(ENDPOINT_RESET_PASSWORD + query).Post().then((response) => {
                                if (response.statusCode === 202) {
                                    showAlert({ show: true, content: "Senha reenviada com sucesso", color: "success", time: 5000 })
                                } else {
                                    if (response.statusCode === 404) {
                                        showAlert({ show: true, content: "E-mail não encontrado", color: "danger", time: 5000 })
                                    } else {
                                        showAlert({ show: true, content: "Ops!!! Algo deu errado tente novamente mais tarde...", color: "danger", time: 5000 })
                                    }
                                }
                            })
                        }
                    }} />
                :
                <ErrorNotAuthorizedPage />
            }
        </>
    )
}
